"use client";

import { useState, useEffect } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@workspace/ui/components/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@workspace/ui/components/form";
import { Input } from "@workspace/ui/components/input";
import { Button } from "@workspace/ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/ui/components/select";
import { api } from "@/trpc/react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const externalUrlSchema = z
  .string()
  .url({ message: "Must be a valid URL" })
  .refine((u) => /^https?:\/\//.test(u), { message: "Must start with http:// or https://" });

const internalPathSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine((p) => p.startsWith('/') && !p.startsWith('//'), { message: "Internal path must start with '/'" });

const linkSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(100, "Name is too long"),
    url: z.union([externalUrlSchema, internalPathSchema]),
    instagramUrl: z.string().url("Must be a valid URL").optional().or(z.literal("")),
    tiktokUrl: z.string().url("Must be a valid URL").optional().or(z.literal("")),
    description: z.string().max(1000, "Description is too long").optional().or(z.literal("")),
    featured: z.boolean().default(false),
    categoryId: z.string().optional(),
  })
  .refine((data) => data.featured || !!(data.categoryId && data.categoryId.length > 0), {
    message: "Category is required unless Featured",
    path: ["categoryId"],
  });

type LinkFormData = z.input<typeof linkSchema>;

interface EditLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: Array<{ id: string; name: string }>;
  link: {
    id: string;
    name: string;
    url: string;
    instagramUrl?: string | null;
    tiktokUrl?: string | null;
    categoryId: string | null;
    description?: string | null;
    featured?: boolean;
  } | null;
}

export function EditLinkDialog({
  open,
  onOpenChange,
  categories,
  link,
}: EditLinkDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const utils = api.useUtils();
  
  const form = useForm<LinkFormData>({
    resolver: zodResolver(linkSchema),
    defaultValues: {
      name: "",
      url: "",
      instagramUrl: "",
      tiktokUrl: "",
      description: "",
      featured: false,
      categoryId: "",
    },
  });
  
  // Reset form when link changes
  useEffect(() => {
    if (link) {
      form.reset({
        name: link.name,
        url: link.url,
        instagramUrl: link.instagramUrl || "",
        tiktokUrl: link.tiktokUrl || "",
        description: link.description || "",
        featured: !!link.featured,
        categoryId: link.categoryId || "",
      });
    }
  }, [link, form]);
  
  const updateLink = api.link.update.useMutation({
    onSuccess: () => {
      utils.link.getAll.invalidate();
      toast.success("Link updated successfully");
      onOpenChange(false);
      form.reset();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update link");
    },
  });
  
  const onSubmit: SubmitHandler<LinkFormData> = async (data) => {
    if (!link) return;
    
    setIsSubmitting(true);
    try {
      const payload = {
        id: link.id,
        name: data.name,
        url: data.url,
        instagramUrl: data.instagramUrl || null,
        tiktokUrl: data.tiktokUrl || null,
        description: data.description?.trim() ? data.description : null,
        featured: !!data.featured,
        categoryId: data.featured ? null : (data.categoryId || null),
      };
      await updateLink.mutateAsync(payload);
    } catch {
      // Error is handled by the mutation onError
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Link</DialogTitle>
          <DialogDescription>
            Update the link details below.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="name"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Example Site"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage>{fieldState.error?.message}</FormMessage>
                </FormItem>
              )}
            />
            
            <FormField
              name="url"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>URL or Internal Path</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="https://example.com or /internal/path"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage>{fieldState.error?.message}</FormMessage>
                </FormItem>
              )}
            />
            
            <FormField
              name="instagramUrl"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Instagram URL (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://example.com/instagram"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage>{fieldState.error?.message}</FormMessage>
                </FormItem>
              )}
            />
            
            <FormField
              name="tiktokUrl"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>TikTok URL (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://example.com/tiktok"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage>{fieldState.error?.message}</FormMessage>
                </FormItem>
              )}
            />

            <FormField
              name="description"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <textarea
                      placeholder="Add a short description for the featured section"
                      className="min-h-20 w-full resize-y rounded-md border bg-background px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      {...field}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage>{fieldState.error?.message}</FormMessage>
                </FormItem>
              )}
            />

            <FormField
              name="featured"
              render={({ field, fieldState }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormLabel className="!mb-0">Featured (no category)</FormLabel>
                  </div>
                  <FormMessage>{fieldState.error?.message}</FormMessage>
                </FormItem>
              )}
            />
            
            <FormField
              name="categoryId"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select 
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={isSubmitting || !!form.watch("featured")}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories && categories.length > 0 ? (
                        categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))
                      ) : (
                        <div className="p-2 text-sm text-muted-foreground">
                          No categories available
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage>{fieldState.error?.message}</FormMessage>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Update Link
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
